<!-- src/app/pages/legislative-tracker/legislative-tracker.html -->
<section class="tracker-page">

  <!-- HERO -->
  <div class="tracker-hero">
    <div class="tracker-eyebrow">Transparency in Action</div>
    <h1>Michigan Legislative Tracker</h1>
    <p>
      Tracking federal bills on digital rights, the environment, healthcare,
      labor, and civil liberties. Built by the candidate.
    </p>
    <div class="tracker-hero__note">
      Data pulled live from the
      <a href="https://api.congress.gov" target="_blank" rel="noopener noreferrer">Congress.gov API</a>.
      Updated continuously.
    </div>
  </div>

  <!-- FILTERS -->
  <div class="tracker-filters">
    <div class="container">
      <div class="filter-row">
        <input
          type="text"
          class="filter-search"
          placeholder="Search by keyword or bill number (e.g. HR-15, S-1503)..."
          [(ngModel)]="searchQuery"
          (keyup.enter)="onSearch()"
        />
        <button class="filter-btn" (click)="onSearch()" [disabled]="isLoading">
          {{ isLoading ? 'Searching...' : 'Search' }}
        </button>
      </div>
      <div class="filter-row filter-row--wrap">
        <div class="filter-tags">
          <button
            class="filter-tag"
            *ngFor="let topic of topics"
            [class.active]="activeTopic === topic.key"
            (click)="setTopic(topic.key)"
          >{{ topic.label }}</button>
        </div>
        <div class="filter-toggles">
          <button
            class="filter-toggle"
            [class.active]="showBookmarks"
            (click)="toggleBookmarks()"
            title="Show bookmarked bills"
          >
            ★ Saved ({{ bookmarks.size }})
          </button>
        </div>
      </div>
    </div>
  </div>

  <!-- SAVE ACT WATCH BANNER -->
  <div class="save-watch-banner">
    <div class="container">
      <div class="save-watch-header" (click)="toggleSaveActWatch()">
        <div class="save-watch-title">
          <span class="save-watch-pulse"></span>
          <span class="save-watch-label">🗳 SAVE Act Watch - Actively Tracking S. 1383 (119th Congress)</span>
        </div>
        <div class="save-watch-meta">
          <span class="save-watch-status">Senate cloture failed 41–49 · Mar 21, 2026 · Not yet law</span>
          <span class="save-watch-toggle">{{ showSaveActWatch ? '▲ Hide' : '▼ Show' }} bill lineage</span>
        </div>
      </div>

      <div class="save-watch-explainer" *ngIf="showSaveActWatch">
        <p class="save-watch-note">
          This bill has been introduced three times under two different names. Here is the complete lineage - so you always know exactly which bill we are tracking and why.
        </p>
        <div class="save-bills-grid">
          <div
            class="save-bill-card"
            *ngFor="let bill of saveActBills; let i = index"
            [class.save-bill-card--active]="i === 0"
          >
            <div class="save-bill-card__header">
              <span class="save-bill-card__number">{{ bill.billType | uppercase }}-{{ bill.billNumber }}</span>
              <span class="save-bill-card__badge" [ngClass]="bill.statusColor">{{ bill.status }}</span>
              <span *ngIf="i === 0" class="save-bill-card__current">▶ Currently Tracked</span>
            </div>
            <p class="save-bill-card__title">{{ bill.title }}</p>
            <div class="save-bill-card__action">
              <span class="save-bill-card__action-date">{{ bill.latestAction.actionDate | date:'mediumDate' }}</span>
              <p>{{ bill.latestAction.text }}</p>
            </div>
            <div class="save-bill-card__footer">
              <span class="bill-position position-opposes">❌ Lydia Opposes</span>
              <a [href]="bill.url" target="_blank" rel="noopener noreferrer" class="save-bill-card__link">
                View on Congress.gov →
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>

  <!-- BILLS -->
  <div class="tracker-body">
    <div class="container">

      <div *ngIf="isLoading" class="tracker-loading">
        <div class="tracker-loading__spinner"></div>
        <p>Loading bills from Congress.gov...</p>
      </div>

      <div *ngIf="!isLoading && filteredBills.length === 0" class="tracker-empty">
        <p *ngIf="showBookmarks">No saved bills yet. Click ★ on any bill to save it.</p>
        <p *ngIf="!showBookmarks">No bills found. Try a different search or topic.</p>
      </div>

      <div *ngIf="!isLoading && filteredBills.length > 0" class="bills-grid">
        <div class="bill-card" *ngFor="let bill of filteredBills">

          <!-- CARD HEADER -->
          <div class="bill-card__header">
            <div class="bill-card__meta">
              <span class="bill-card__number">{{ bill.billType | uppercase }}-{{ bill.billNumber }}</span>
              <span class="bill-card__status" [ngClass]="bill.statusColor">{{ bill.status }}</span>
            </div>
            <div class="bill-card__actions">
              <!-- Lydia's position -->
              <span
                *ngIf="bill.lydiaPosition"
                class="bill-position"
                [ngClass]="'position-' + bill.lydiaPosition"
                [title]="'Lydia ' + bill.lydiaPosition + ' this bill'"
              >
                <span *ngIf="bill.lydiaPosition === 'supports'">✅ Lydia Supports</span>
                <span *ngIf="bill.lydiaPosition === 'opposes'">❌ Lydia Opposes</span>
                <span *ngIf="bill.lydiaPosition === 'watching'">👀 Lydia Watching</span>
              </span>
              <!-- Bookmark button -->
              <button
                class="bill-bookmark"
                [class.bookmarked]="bookmarks.has(bill.billType + '-' + bill.billNumber)"
                (click)="toggleBookmark(bill)"
                [title]="bookmarks.has(bill.billType + '-' + bill.billNumber) ? 'Remove bookmark' : 'Save bill'"
                aria-label="Bookmark bill"
              >★</button>
              <!-- Share button -->
              <button
                class="bill-share"
                (click)="shareBill(bill)"
                title="Share this bill"
                aria-label="Share bill"
              >⬆</button>
            </div>
          </div>

          <!-- TITLE -->
          <h3 class="bill-card__title">{{ bill.title }}</h3>

          <!-- POLICY AREA -->
          <div class="bill-card__area" *ngIf="bill.policyArea">{{ bill.policyArea }}</div>

          <!-- LATEST ACTION -->
          <div class="bill-card__action" *ngIf="bill.latestAction && bill.latestAction.text">
            <span class="bill-card__action-label">Latest Action</span>
            <span class="bill-card__action-date">{{ bill.latestAction.actionDate | date:'mediumDate' }}</span>
            <p>{{ bill.latestAction.text }}</p>
          </div>

          <!-- FOOTER -->
          <div class="bill-card__footer">
            <span class="bill-card__date">Introduced: {{ bill.introducedDate | date:'mediumDate' }}</span>
            <a [href]="bill.url" target="_blank" rel="noopener noreferrer" class="bill-card__link">
              View on Congress.gov
            </a>
          </div>

        </div>
      </div>

    </div>
  </div>

  <!-- LYDIA'S COMMITMENT -->
  <div class="tracker-pledge">
    <div class="container">
      <h2>Lydia's Commitment</h2>
      <p>
        When elected, Lydia will maintain a public tracker of every vote she casts in the Senate,
        with plain-English explanations of why she voted the way she did. No spin. No party-line
        excuses. Full transparency to every Michigander.
      </p>
      <a routerLink="/issues" class="tracker-pledge__btn">See Lydia's Full Platform</a>
    </div>
  </div>

</section>

<!-- SHARE TOAST -->
<div class="share-toast" [class.visible]="shareToastVisible">
  Link copied to clipboard!
</div>
